export interface AepCheckRequest {
  // Define request structure based on API requirements
}

export interface AepCheckResponse {
  isAep: boolean
  quoteDate?: string
  currentDate?: string
  nextEffectiveDate?: string
}

export class AepService {
  private static getBaseUrl(): string {
    // In development, use proxy path to avoid CORS issues
    // In production, use the full URL
    return import.meta.env.DEV
      ? '/sapps/buyonline/rest/applicationService'
      : `${import.meta.env.VITE_BCBSNC_URL}/sapps/buyonline/rest/applicationService`
  }

  static async doAepCheck(): Promise<AepCheckResponse> {
    try {
      const response = await fetch(`${this.getBaseUrl()}/doAepCheck`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`AEP check request failed: ${response.statusText}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text()
        console.error('Non-JSON response received:', text.substring(0, 200))
        throw new Error('Server returned non-JSON response')
      }

      return await response.json()
    } catch (error) {
      console.error('Error checking AEP:', error)
      // Return default values if AEP check fails
      return {
        isAep: false,
        currentDate: new Date().toISOString(),
      }
    }
  }

  static getEffectiveDateOptions(aepData: AepCheckResponse | null): string[] {
    let now = new Date()
    if (aepData?.quoteDate) {
      now = new Date(aepData.quoteDate)
    } else if (aepData?.currentDate) {
      // forcing application to use the server date to calculate the effective dates, rather than client date
      now = new Date(aepData.currentDate)
    }

    const current = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      1
    ).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })

    const next = new Date(
      now.getFullYear(),
      now.getMonth() + 2,
      1
    ).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })

    const uiEffectiveDateList = [current, next]

    if (aepData?.isAep) {
      // Handle Jan or Feb eff dates for AEP
      let nextYearEffDate = new Date(now.getFullYear() + 1, 0, 1)
      if (now.getMonth() === 0) {
        nextYearEffDate = new Date(now.getFullYear(), 1, 1)
      }
      const nextYearEffDateStr = nextYearEffDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
      if (
        now.getTime() <= nextYearEffDate.getTime() &&
        !uiEffectiveDateList.includes(nextYearEffDateStr)
      ) {
        uiEffectiveDateList.push(nextYearEffDateStr)
      }
    }

    return uiEffectiveDateList
  }
}
